import { ClientCombobox } from "@/components/ClientCombobox";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { DropZone } from "@/components/DropZone";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, API, euro, formatApiErrorDetail, fmtDate, openAuthPdf } from "@/lib/api";
import { useSuspendedClientGuard } from "@/context/SuspendedClientAlert";
import { InternalLayout } from "@/components/InternalLayout";
import { useUnsavedGuard } from "@/context/UnsavedChangesGuard";
import { StatusBadge, TypeBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, FloppyDiskIcon as FloppyDisk, SignOutIcon as SignOut, TrashIcon as Trash, PaperclipIcon as Paperclip, UploadIcon as Upload, ClockIcon as Clock, WarningCircleIcon as WarningCircle, ArrowSquareOutIcon as ArrowSquareOut, FilePdfIcon as FilePdf, PrinterIcon as Printer, PlusIcon as Plus, ReceiptIcon as Receipt } from "@phosphor-icons/react";
import { CATEGORIA_DEFAULT_TIPO, TIPI_PER_CATEGORIA, MODELLI_PER_MARCA, PERIODICITA, PERIODICITA_FLAT, CAMPI_SPECIFICI } from "@/lib/contratti";
import { ContractRicariche } from "@/components/ContractRicariche";
import { ContractSheet } from "@/components/CopyCostContracts";

const CC_TIP_LABEL = { solo_costo_copia: "Solo costo copia", canone_costo_copia: "Canone + costo copia", canone_incluse_eccedenze: "Canone + incluse + eccedenze" };
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const EMPTY = {
  cliente_id: "", nome: "", nome_extra: "", categoria: "", tipo: "", periodicita: "unica",
  data_inizio: "", data_scadenza: "", rinnovo_automatico: true, stato: "attivo",
  visibile_cliente: true, importo: 0, note: "", storico_note: "", dettagli: {},
  ubicazione: "", piano: "", ascensore: "", spese_extra: [], sottocontratti: [],
  canone_items: [], consumo_config: null, fotocopiatrici: [], ore_acquistate: 0, prezzo_unitario_ora: 0,
};

export default function ContractDetail() {
  const { id } = useParams();
  const isNew = !id || id === "nuovo";
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [catNames, setCatNames] = useState({});
  const [orig, setOrig] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useSuspendedClientGuard(form?.cliente_id);
  const [moAlert, setMoAlert] = useState(true);
  const [copierCfg, setCopierCfg] = useState({ marche: [], modelli: {} });
  const fileRef = useRef(null);
  const [ccDevices, setCcDevices] = useState(null);
  const [ccSheetOpen, setCcSheetOpen] = useState(false);
  const [interventi, setInterventi] = useState([]);
  const [annoSotto, setAnnoSotto] = useState("all");
  const anniSotto = useMemo(() => {
    const ys = new Set();
    (form?.sottocontratti || []).forEach((s) => { if (s.anno) ys.add(String(s.anno)); });
    return Array.from(ys).sort().reverse();
  }, [form?.sottocontratti]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/contratti-config").then(({ data }) => {
      const m = {}; (data.categorie || []).forEach((c) => { m[c.slug] = c.nome; }); setCatNames(m);
      setCopierCfg({ marche: data.copier_marche || [], modelli: data.copier_modelli || {} });
    });
  }, []);

  const init = useCallback(() => {
    if (isNew) {
      const cat = sp.get("categoria") || "";
      const clienteId = sp.get("cliente_id") || "";
      const base = { ...EMPTY, categoria: cat, tipo: CATEGORIA_DEFAULT_TIPO[cat] || "", cliente_id: clienteId };
      if (cat === "noleggio_costo_copia") base.dettagli = { ...(base.dettagli || {}), marca_fotocopiatrice: "Ricoh" };
      setForm(base); setOrig(JSON.stringify(base));
    } else {
      api.get(`/contracts/${id}`).then(({ data }) => {
        const merged = { ...EMPTY, ...data, dettagli: data.dettagli || {} };
        setForm(merged); setOrig(JSON.stringify(merged));
        setMoAlert(data.alert_monte_ore_enabled !== false);
      }).catch(() => { toast.error("Contratto non trovato"); nav("/contratti"); });
    }
  }, [id, isNew, sp, nav]);
  useEffect(() => { init(); }, [init]);
  useEffect(() => {
    if (!isNew && id) api.get(`/contracts/${id}/interventions`).then(({ data }) => setInterventi(data)).catch(() => {});
  }, [id, isNew]);

  const loadCcDevices = useCallback(() => {
    if (isNew || !id) return;
    api.get(`/copy-cost-contracts/${id}`).then(({ data }) => setCcDevices(data.devices || [])).catch(() => setCcDevices([]));
  }, [id, isNew]);
  useEffect(() => {
    if (!isNew && form?.categoria === "noleggio_costo_copia") loadCcDevices();
  }, [isNew, form?.categoria, loadCcDevices]);

  const reloadValorizzazione = useCallback(() => {
    if (isNew || !id) return;
    api.get(`/contracts/${id}`).then(({ data }) => {
      setForm((f) => (f ? { ...f, valorizzazione: data.valorizzazione, monte_ore: data.monte_ore, consumo: data.consumo, ore_acquistate: data.ore_acquistate } : f));
    }).catch(() => {});
    api.get(`/contracts/${id}/interventions`).then(({ data }) => setInterventi(data)).catch(() => {});
  }, [id, isNew]);

  const dirty = useMemo(() => form && orig && JSON.stringify(form) !== orig, [form, orig]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updDet = (k, v) => setForm((f) => ({ ...f, dettagli: { ...f.dettagli, [k]: v } }));

  const addSpesa = () => { const d = new Date(); const loc = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; setForm((f) => ({ ...f, spese_extra: [...(f.spese_extra || []), { descrizione: "", importo: "", data: loc }] })); };
  const updSpesa = (i, k, v) => setForm((f) => { const arr = [...(f.spese_extra || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...f, spese_extra: arr }; });
  const delSpesa = (i) => setForm((f) => ({ ...f, spese_extra: (f.spese_extra || []).filter((_, j) => j !== i) }));
  const speseTot = (form?.spese_extra || []).reduce((s, x) => s + (Number(x.importo) || 0), 0);

  const addSotto = () => { const d = new Date(); setForm((f) => ({ ...f, sottocontratti: [...(f.sottocontratti || []), { anno: d.getFullYear(), mese: d.getMonth() + 1, importo: "", monte_ore_massimo: "" }] })); };
  const updSotto = (i, k, v) => setForm((f) => { const arr = [...(f.sottocontratti || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...f, sottocontratti: arr }; });
  const delSotto = (i) => setForm((f) => ({ ...f, sottocontratti: (f.sottocontratti || []).filter((_, j) => j !== i) }));
  const splitEqualSotto = () => setForm((f) => {
    const arr = [...(f.sottocontratti || [])]; const n = arr.length; if (!n) return f;
    const tot = Number(f.importo) || 0; const each = Math.round((tot / n) * 100) / 100;
    return { ...f, sottocontratti: arr.map((s, idx) => ({ ...s, importo: idx === n - 1 ? Math.round((tot - each * (n - 1)) * 100) / 100 : each })) };
  });
  const sottoTot = (form?.sottocontratti || []).reduce((s, x) => s + (Number(x.importo) || 0), 0);
  const sottoVal = useMemo(() => Object.fromEntries(((form?.consumo?.sottocontratti_valorizzati) || []).map((v) => [`${v.anno}-${v.mese}`, v])), [form?.consumo]);
  const addSpesaSotto = (i) => { const d = new Date(); const loc = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; setForm((f) => { const arr = [...(f.sottocontratti || [])]; arr[i] = { ...arr[i], spese_extra: [...(arr[i].spese_extra || []), { descrizione: "", importo: "", data: loc }] }; return { ...f, sottocontratti: arr }; }); };
  const updSpesaSotto = (i, j, k, v) => setForm((f) => { const arr = [...(f.sottocontratti || [])]; const se = [...(arr[i].spese_extra || [])]; se[j] = { ...se[j], [k]: v }; arr[i] = { ...arr[i], spese_extra: se }; return { ...f, sottocontratti: arr }; });
  const delSpesaSotto = (i, j) => setForm((f) => { const arr = [...(f.sottocontratti || [])]; arr[i] = { ...arr[i], spese_extra: (arr[i].spese_extra || []).filter((_, k) => k !== j) }; return { ...f, sottocontratti: arr }; });

  // Periodicità: per i FLAT solo Mensile / A progetto (aggiunge l'eventuale valore legacy per non rompere i dati)
  const periodicitaOpts = useMemo(() => {
    if (form?.categoria !== "flat") return PERIODICITA;
    const base = [...PERIODICITA_FLAT];
    if (form.periodicita && !base.some((p) => p.v === form.periodicita)) {
      base.push({ v: form.periodicita, l: form.periodicita });
    }
    return base;
  }, [form?.categoria, form?.periodicita]);
  const isFlat = form?.categoria === "flat";

  const onCategoria = (v) => {
    const cat = v === "none" ? "" : v;
    const allowed = TIPI_PER_CATEGORIA[cat] || TIPI_PER_CATEGORIA[""];
    setForm((f) => ({
      ...f, categoria: cat,
      tipo: allowed.includes(f.tipo) ? f.tipo : (CATEGORIA_DEFAULT_TIPO[cat] || allowed[0] || ""),
      dettagli: cat === "noleggio_costo_copia" ? { ...(f.dettagli || {}), marca_fotocopiatrice: (f.dettagli || {}).marca_fotocopiatrice || "Ricoh" } : f.dettagli,
    }));
  };

  const buildPayload = () => {
    const p = { ...form };
    p.importo = Number(form.importo) || 0;
    p.ore_acquistate = Number(form.ore_acquistate) || 0;
    p.prezzo_unitario_ora = Number(form.prezzo_unitario_ora) || 0;
    p.spese_extra = (form.spese_extra || []).map((s) => ({ descrizione: s.descrizione || "", importo: Number(s.importo) || 0, data: s.data || "" }));
    p.sottocontratti = (form.sottocontratti || []).map((s) => ({ anno: Number(s.anno) || 0, mese: Number(s.mese) || 0, importo: Number(s.importo) || 0, monte_ore_massimo: Number(s.monte_ore_massimo) || 0, concluso_fatturato: !!s.concluso_fatturato, spese_extra: (s.spese_extra || []).map((x) => ({ descrizione: x.descrizione || "", importo: Number(x.importo) || 0, data: x.data || "" })) }));
    delete p.id; delete p.created_at; delete p._id; delete p.cliente_nome; delete p.monte_ore; delete p.allegati;
    return p;
  };

  const save = async (exit = false) => {
    if (!form.cliente_id) { toast.error("Seleziona un cliente"); return false; }
    setSaving(true);
    try {
      let data;
      if (isNew) { ({ data } = await api.post("/contracts", buildPayload())); }
      else { ({ data } = await api.put(`/contracts/${id}`, buildPayload())); }
      toast.success(isNew ? "Contratto creato" : "Contratto aggiornato");
      if (exit) { nav(-1); return true; }
      const merged = { ...EMPTY, ...data, dettagli: data.dettagli || {} };
      setForm(merged); setOrig(JSON.stringify(merged));
      if (isNew) nav(`/contratti/${data.id}`, { replace: true });
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
    finally { setSaving(false); }
  };

  const guardedNavigate = useUnsavedGuard(dirty, () => save(false));

  const toggleMoAlert = async (v) => {
    setMoAlert(v);
    try {
      await api.patch(`/contracts/${id}/alert-monte-ore`, { enabled: v });
      toast.success(v ? "Avvisi saldo basso attivati" : "Avvisi saldo basso disattivati");
    } catch (e) { setMoAlert(!v); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare il contratto "${form.nome}"?`)) return;
    try { await api.delete(`/contracts/${id}`); toast.success("Contratto eliminato"); nav("/contratti"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const fd = new FormData(); files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/contracts/${id}/allegati`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, allegati: data.allegati })); toast.success("Allegato caricato");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    e.target.value = "";
  };
  const delAllegato = async (aid) => {
    if (!window.confirm("Eliminare l'allegato?")) return;
    try { const { data } = await api.delete(`/contracts/${id}/allegati/${aid}`); setForm((f) => ({ ...f, allegati: data.allegati })); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!form) return <InternalLayout><div className="p-12 text-muted-foreground">Caricamento…</div></InternalLayout>;

  const spec = CAMPI_SPECIFICI[form.categoria];
  const tipiAllowed = TIPI_PER_CATEGORIA[form.categoria] || TIPI_PER_CATEGORIA[""];

  const renderField = (f) => {
    const scope = f.scope === "root" ? form : (form.dettagli || {});
    const val = scope[f.key];
    const set = (v) => (f.scope === "root" ? upd(f.key, v) : updDet(f.key, v));
    const testid = `contract-field-${f.key}`;
    if (f.key === "marca_fotocopiatrice") {
      const marcheOpts = copierCfg.marche.length ? copierCfg.marche : (f.options || []).map((o) => o.v);
      return (
        <div key={f.key}><Label className="text-xs uppercase tracking-wider">{f.label}</Label>
          <Select value={val || ""} onValueChange={(v) => setForm((ff) => ({ ...ff, dettagli: { ...(ff.dettagli || {}), marca_fotocopiatrice: v, modello_fotocopiatrice: "" } }))}><SelectTrigger className="mt-1 rounded-sm" data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{marcheOpts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
      );
    }
    if (f.type === "select_modello") {
      const marca = (form.dettagli || {}).marca_fotocopiatrice;
      const opts = ((copierCfg.modelli[marca] && copierCfg.modelli[marca].length) ? copierCfg.modelli[marca] : (MODELLI_PER_MARCA[marca] || [])).slice().sort((a, b) => (a || "").localeCompare(b || "", "it", { sensitivity: "base" }));
      return (
        <div key={f.key}><Label className="text-xs uppercase tracking-wider">{f.label}</Label>
          <Select value={val || ""} onValueChange={set} disabled={!marca}><SelectTrigger className="mt-1 rounded-sm" data-testid={testid}><SelectValue placeholder={marca ? "—" : "Seleziona prima la marca"} /></SelectTrigger>
            <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
      );
    }
    if (f.type === "switch")
      return (
        <div key={f.key} className="flex items-center gap-3 mt-5"><Switch checked={!!val} onCheckedChange={set} data-testid={testid} /><Label className="text-sm">{f.label}</Label></div>
      );
    if (f.type === "textarea")
      return (
        <div key={f.key} className={f.full ? "sm:col-span-2" : ""}><Label className="text-xs uppercase tracking-wider">{f.label}</Label>
          <Textarea value={val || ""} onChange={(e) => set(e.target.value)} className="mt-1 rounded-sm" rows={2} data-testid={testid} /></div>
      );
    if (f.type === "select")
      return (
        <div key={f.key}><Label className="text-xs uppercase tracking-wider">{f.label}</Label>
          <Select value={val || f.default || ""} onValueChange={set}><SelectTrigger className="mt-1 rounded-sm" data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{f.options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent></Select></div>
      );
    return (
      <div key={f.key}><Label className="text-xs uppercase tracking-wider">{f.label}</Label>
        <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={f.type === "date" ? (val || "").slice(0, 10) : (val ?? "")}
          onChange={(e) => set(f.type === "number" ? e.target.value : e.target.value)} className="mt-1 rounded-sm" data-testid={testid} /></div>
    );
  };

  return (
    <InternalLayout>
      <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="contract-detail-page">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" data-testid="contract-back-btn" onClick={() => (guardedNavigate ? guardedNavigate(-1) : nav(-1))} className="h-8 w-8"><ArrowLeft size={18} /></Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-xl font-bold truncate">{form.nome ? `${form.nome}${form.nome_extra ? ` ${form.nome_extra}` : ""}` : "Nuovo contratto"}</h1>
              <StatusBadge status={form.stato} />
              {form.categoria && <Badge variant="outline" className="rounded-sm bg-primary/10 text-primary border-primary/20" data-testid="contract-badge-categoria">{catNames[form.categoria] || form.categoria}</Badge>}
              {form.tipo && <TypeBadge tipo={form.tipo} />}
              {dirty && <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium" data-testid="contract-unsaved-badge"><WarningCircle size={14} weight="fill" /> Modifiche non salvate</span>}
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">{clients.find((c) => c.id === form.cliente_id)?.ragione_sociale || form.cliente_nome || "Nessun cliente"}</div>
          </div>
          {!isNew && <Button variant="ghost" size="icon" onClick={remove} data-testid="contract-delete-btn" className="h-9 w-9 text-muted-foreground hover:text-destructive"><Trash size={18} /></Button>}
          <Button variant="outline" onClick={() => save(true)} disabled={saving} className="rounded-sm gap-2" data-testid="contract-saveexit-btn"><SignOut size={16} /> Salva ed esci</Button>
          <Button onClick={() => save(false)} disabled={saving} className="rounded-sm gap-2" data-testid="contract-save-btn"><FloppyDisk size={16} /> Salva</Button>
        </div>

        <div className="space-y-6">
          {form.consumo && _consumoAlert(form.consumo) && (
            <div className="rounded-sm border border-amber-300 bg-amber-50 text-amber-800 px-4 py-3 flex items-center gap-2 text-sm" data-testid="contract-consumo-alert">
              <WarningCircle size={18} weight="fill" /> {_consumoAlert(form.consumo)}
            </div>
          )}
          {!isNew && form.categoria === "assistenza" && (
            <Card className="rounded-sm border-border shadow-none p-5" data-testid="contract-mo-alert-section">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <WarningCircle size={20} weight="bold" className="text-primary mt-0.5" />
                  <div>
                    <h3 className="font-heading font-semibold text-sm">Avvisi saldo basso</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Notifica in-app ed email quando le ore prepagate scendono sotto la soglia o si esauriscono. Disattivalo per silenziare gli avvisi solo di questo contratto.</p>
                  </div>
                </div>
                <Switch checked={moAlert} onCheckedChange={toggleMoAlert} data-testid="contract-mo-alert-switch" />
              </div>
            </Card>
          )}
          {/* Dati generali */}
          <Card className="rounded-sm border-border shadow-none p-5">
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-primary">Dati generali</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-xs uppercase tracking-wider">Codice contratto</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input data-testid="contract-nome" value={form.nome} readOnly disabled className="rounded-sm bg-secondary/50 flex-shrink-0 w-auto min-w-[160px]" placeholder={isNew ? "Generato al salvataggio" : ""} />
                  <Input data-testid="contract-nome-extra" value={form.nome_extra || ""} onChange={(e) => upd("nome_extra", e.target.value)} className="rounded-sm flex-1" placeholder="Aggiungi testo (es. descrizione)…" />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Codice generato automaticamente (NOMECLIENTE-XX_SUFFIX). Aggiungi parole nel campo a destra.</p></div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Cliente *</Label>
                <div className="mt-1"><ClientCombobox clients={clients} value={form.cliente_id} onChange={(v) => upd("cliente_id", v)} placeholder="Seleziona" testid="contract-cliente" /></div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Categoria</Label>
                <Select value={form.categoria || "none"} onValueChange={onCategoria}><SelectTrigger className="mt-1 rounded-sm" data-testid="contract-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Non categorizzato</SelectItem>{Object.entries(catNames).map(([slug, nome]) => <SelectItem key={slug} value={slug}>{nome}</SelectItem>)}</SelectContent></Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Tipo contratto</Label>
                <Select value={form.tipo} onValueChange={(v) => upd("tipo", v)}><SelectTrigger className="mt-1 rounded-sm" data-testid="contract-tipo"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{tipiAllowed.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Stato</Label>
                <Select value={form.stato} onValueChange={(v) => upd("stato", v)}><SelectTrigger className="mt-1 rounded-sm" data-testid="contract-stato"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="attivo">Attivo</SelectItem><SelectItem value="da_rinnovare">Da rinnovare</SelectItem><SelectItem value="sospeso">Sospeso</SelectItem><SelectItem value="cessato">Cessato</SelectItem></SelectContent></Select>
              </div>
              {form.categoria !== "noleggio_costo_copia" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider">{form.categoria === "flat" ? "Tipologia FLAT" : "Periodicità"}</Label>
                  <Select value={form.periodicita} onValueChange={(v) => upd("periodicita", v)}><SelectTrigger className="mt-1 rounded-sm" data-testid="contract-periodicita"><SelectValue /></SelectTrigger>
                    <SelectContent>{periodicitaOpts.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent></Select>
                </div>
              )}
              <div><Label className="text-xs uppercase tracking-wider">Data inizio</Label><Input type="date" value={(form.data_inizio || "").slice(0, 10)} onChange={(e) => upd("data_inizio", e.target.value)} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Data fine / scadenza</Label><Input type="date" value={(form.data_scadenza || "").slice(0, 10)} onChange={(e) => upd("data_scadenza", e.target.value)} className="mt-1 rounded-sm" /></div>
              {spec?.fields?.some((f) => f.key === "ubicazione") && (() => {
                const cli = clients.find((c) => c.id === form.cliente_id);
                const fmt = (a, cap, city) => [a, [cap, city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
                const opts = [];
                if (cli) {
                  const main = fmt(cli.indirizzo, cli.cap, cli.citta);
                  if (main) opts.push({ label: `Sede principale — ${main}`, value: main });
                  (cli.sedi || []).forEach((s) => {
                    const addr = fmt(s.indirizzo, s.cap, s.citta);
                    if (addr) opts.push({ label: `${s.nome || "Sede"} — ${addr}`, value: addr });
                  });
                }
                return (
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Ubicazione</Label>
                    <Select value={form.ubicazione || ""} onValueChange={(v) => upd("ubicazione", v)}>
                      <SelectTrigger className="mt-1 rounded-sm" data-testid="contract-ubicazione"><SelectValue placeholder={opts.length ? "Seleziona sede" : "Nessun indirizzo in anagrafica"} /></SelectTrigger>
                      <SelectContent>
                        {form.ubicazione && !opts.some((o) => o.value === form.ubicazione) && <SelectItem value={form.ubicazione}>{form.ubicazione}</SelectItem>}
                        {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })()}
              {spec?.fields?.some((f) => f.key === "ubicazione") && (
                <div><Label className="text-xs uppercase tracking-wider">Piano</Label><Input value={form.piano || ""} onChange={(e) => upd("piano", e.target.value)} placeholder="Es. Piano terra, 2° piano…" className="mt-1 rounded-sm" data-testid="contract-piano" /></div>
              )}
              {spec?.fields?.some((f) => f.key === "ubicazione") && (
                <div><Label className="text-xs uppercase tracking-wider">Ascensore</Label>
                  <Select value={form.ascensore || ""} onValueChange={(v) => upd("ascensore", v)}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="contract-ascensore"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="si">Sì</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.categoria !== "noleggio_costo_copia" && (
                <div><Label className="text-xs uppercase tracking-wider">{form.categoria === "flat" ? "Ricavo FLAT (€)" : "Importo periodo (€)"}</Label><Input type="number" step="0.01" data-testid="contract-importo" value={form.importo} onChange={(e) => upd("importo", e.target.value)} className="mt-1 rounded-sm" /></div>
              )}
              <div className="col-span-1 sm:col-span-2"><Label className="text-xs uppercase tracking-wider">Note interne</Label><Textarea value={form.note} onChange={(e) => upd("note", e.target.value)} className="mt-1 rounded-sm" rows={2} /></div>
            </div>
          </Card>

          {/* Spese Extra (nascoste sui FLAT che usano sottocontratti e sui noleggio/costo copia) */}
          {!isNew && form.categoria !== "noleggio_costo_copia" && !(isFlat && (form.sottocontratti || []).length > 0) && (
            <Card className="rounded-sm border-border shadow-none p-5" data-testid="contract-spese-extra-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-primary flex items-center gap-2"><Receipt size={16} /> Spese extra</h3>
                <Button variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="contract-add-spesa-btn" onClick={addSpesa}><Plus size={14} /> Aggiungi voce</Button>
              </div>
              {(form.spese_extra || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Nessuna spesa extra. Le voci aggiunte vengono detratte dal valore rendicontato.</p>
              ) : (
                <div className="space-y-2">
                  {(form.spese_extra || []).map((s, i) => (
                    <div key={i} className="flex flex-wrap items-end gap-2" data-testid="contract-spesa-row">
                      <div className="flex-1 min-w-[160px]">
                        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Descrizione</Label>
                        <Input value={s.descrizione || ""} onChange={(e) => updSpesa(i, "descrizione", e.target.value)} placeholder="Es. Trasferta, materiale…" className="mt-1 rounded-sm" data-testid="contract-spesa-descrizione" />
                      </div>
                      <div className="w-32">
                        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Importo (€)</Label>
                        <Input type="number" step="0.01" min="0" value={s.importo} onChange={(e) => updSpesa(i, "importo", e.target.value)} className="mt-1 rounded-sm" data-testid="contract-spesa-importo" />
                      </div>
                      <div className="w-40">
                        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Data</Label>
                        <Input type="date" value={(s.data || "").slice(0, 10)} onChange={(e) => updSpesa(i, "data", e.target.value)} className="mt-1 rounded-sm" data-testid="contract-spesa-data" />
                      </div>
                      <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-destructive" onClick={() => delSpesa(i)} data-testid="contract-spesa-delete"><Trash size={15} /></Button>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2 border-t border-border mt-2 text-sm font-medium" data-testid="contract-spese-extra-totale">
                    Totale spese extra: <span className="ml-2 tabular-nums text-rose-600">− {euro(speseTot)}</span>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Sottocontratti mensili (tutti i contratti FLAT) */}
          {!isNew && isFlat && (
            <Card className="rounded-sm border-border shadow-none p-5" data-testid="contract-sottocontratti-section">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-primary flex items-center gap-2"><Clock size={16} /> Sottocontratti mensili</h3>
                <div className="flex items-center gap-2">
                  {anniSotto.length > 0 && (
                    <Select value={annoSotto} onValueChange={setAnnoSotto}>
                      <SelectTrigger className="h-9 w-[140px] rounded-sm text-xs" data-testid="sottocontratti-anno-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutti gli anni</SelectItem>
                        {anniSotto.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Button variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="contract-split-sotto-btn" onClick={splitEqualSotto} disabled={(form.sottocontratti || []).length === 0}>Dividi equamente</Button>
                  <Button variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="contract-add-sotto-btn" onClick={addSotto}><Plus size={14} /> Aggiungi mese</Button>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mb-3">Ogni mese è un periodo a sé: importo e monte ore propri. Le ore e la tariffa effettiva sono calcolate sugli interventi del mese.</p>
              {(form.sottocontratti || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Nessun sottocontratto. Aggiungi i mesi che compongono il contratto.</p>
              ) : (
                <div className="space-y-2">
                  {(form.sottocontratti || []).map((s, i) => {
                    if (annoSotto !== "all" && String(s.anno) !== annoSotto) return null;
                    const v = sottoVal[`${Number(s.anno)}-${Number(s.mese)}`];
                    const codice = s.codice || ((form.nome && Number(s.anno) && Number(s.mese)) ? `${form.nome}/${Number(s.anno)}-${String(Number(s.mese)).padStart(2, "0")}` : "");
                    return (
                      <div key={i} className="rounded-sm border border-border p-2.5" data-testid="contract-sotto-row">
                        {codice && <div className="font-mono text-[11px] text-primary mb-1.5" data-testid="contract-sotto-codice">{codice}</div>}
                        <div className="flex flex-wrap items-end gap-2">
                          <div className="w-40">
                            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Mese</Label>
                            <Select value={String(s.mese || "")} onValueChange={(val) => updSotto(i, "mese", Number(val))}>
                              <SelectTrigger className="mt-1 rounded-sm" data-testid="contract-sotto-mese"><SelectValue placeholder="Mese" /></SelectTrigger>
                              <SelectContent>{MESI.map((m, idx) => <SelectItem key={idx + 1} value={String(idx + 1)}>{m}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="w-28">
                            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Anno</Label>
                            <Input type="number" value={s.anno || ""} onChange={(e) => updSotto(i, "anno", e.target.value)} className="mt-1 rounded-sm" data-testid="contract-sotto-anno" />
                          </div>
                          <div className="w-32">
                            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Importo (€)</Label>
                            <Input type="number" step="0.01" min="0" value={s.importo} onChange={(e) => updSotto(i, "importo", e.target.value)} className="mt-1 rounded-sm" data-testid="contract-sotto-importo" />
                          </div>
                          <div className="w-32">
                            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Monte ore max</Label>
                            <Input type="number" step="0.5" min="0" value={s.monte_ore_massimo} onChange={(e) => updSotto(i, "monte_ore_massimo", e.target.value)} className="mt-1 rounded-sm" data-testid="contract-sotto-monteore" />
                          </div>
                          <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-destructive" onClick={() => delSotto(i)} data-testid="contract-sotto-delete"><Trash size={15} /></Button>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Switch checked={!!s.concluso_fatturato} onCheckedChange={(v) => updSotto(i, "concluso_fatturato", v)} data-testid="contract-sotto-concluso" />
                          <Label className="text-xs">Concluso e fatturato {s.concluso_fatturato && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 border border-emerald-200 font-medium">non mostrato negli interventi</span>}</Label>
                        </div>
                        <div className="mt-2 pt-2 border-t border-border">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Spese extra del mese</span>
                            <Button variant="outline" size="sm" className="h-7 rounded-sm gap-1 text-xs" onClick={() => addSpesaSotto(i)} data-testid="contract-sotto-add-spesa"><Plus size={12} /> Aggiungi spesa</Button>
                          </div>
                          {(s.spese_extra || []).length === 0 ? (
                            <p className="text-[11px] text-muted-foreground">Nessuna spesa extra. Le voci vengono detratte dal valore del mese.</p>
                          ) : (
                            <div className="space-y-1.5">
                              {(s.spese_extra || []).map((sp, j) => (
                                <div key={j} className="flex flex-wrap items-end gap-2" data-testid="contract-sotto-spesa-row">
                                  <div className="flex-1 min-w-[140px]"><Input value={sp.descrizione || ""} onChange={(e) => updSpesaSotto(i, j, "descrizione", e.target.value)} placeholder="Descrizione" className="rounded-sm h-8 text-sm" data-testid="contract-sotto-spesa-desc" /></div>
                                  <div className="w-28"><Input type="number" step="0.01" min="0" value={sp.importo} onChange={(e) => updSpesaSotto(i, j, "importo", e.target.value)} placeholder="€" className="rounded-sm h-8 text-sm" data-testid="contract-sotto-spesa-importo" /></div>
                                  <div className="w-36"><Input type="date" value={(sp.data || "").slice(0, 10)} onChange={(e) => updSpesaSotto(i, j, "data", e.target.value)} className="rounded-sm h-8 text-sm" data-testid="contract-sotto-spesa-data" /></div>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => delSpesaSotto(i, j)} data-testid="contract-sotto-spesa-delete"><Trash size={14} /></Button>
                                </div>
                              ))}
                              <div className="text-[11px] text-rose-600 text-right" data-testid="contract-sotto-spese-totale">Spese mese: − {euro((s.spese_extra || []).reduce((a, x) => a + (Number(x.importo) || 0), 0))}</div>
                            </div>
                          )}
                        </div>
                        {v && (
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 pt-2 border-t border-border text-[11px]" data-testid="contract-sotto-valorizzazione">
                            <span className="text-rose-600" data-testid="contract-sotto-ore-previste">Ore previste: <span className="font-medium">{v.ore_previste ?? 0}h</span></span>
                            <span className="text-muted-foreground">Ore completate: <span className={`font-medium ${v.superato ? "text-rose-600" : "text-foreground"}`}>{v.ore_valide}h</span></span>
                            {v.monte_ore_massimo > 0 && <span className="text-muted-foreground">Ore residue: <span className={`font-medium ${v.superato ? "text-rose-600" : "text-foreground"}`}>{v.ore_residue}h</span></span>}
                            {v.spese_extra_totale > 0 && <span className="text-muted-foreground">Rendic. netto: <span className="font-medium">{euro(v.valore_rendicontato)}</span> <span className="text-rose-600">(− {euro(v.spese_extra_totale)})</span></span>}
                            <span className="text-muted-foreground">Tariffa eff.: <span className="font-medium">{v.tariffa_oraria_effettiva != null ? `${euro(v.tariffa_oraria_effettiva)}/h` : "—"}</span></span>
                            {v.superato && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200 font-medium"><WarningCircle size={11} weight="fill" /> +{v.ore_superamento}h</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="flex justify-end pt-2 border-t border-border mt-2 text-sm" data-testid="contract-sotto-totale">
                    Totale sottocontratti: <span className="ml-2 tabular-nums font-medium">{euro(sottoTot)}</span>
                    {Math.abs(sottoTot - (Number(form.importo) || 0)) >= 0.01 && <span className="ml-2 text-[11px] text-amber-600">(Ricavo FLAT: {euro(Number(form.importo) || 0)})</span>}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Campi specifici categoria */}
          {(() => {
            if (!spec) return null;
            const isPrepaid = form.categoria === "assistenza" && form.tipo === "Contratto di Assistenza Prepagato";
            // Per un contratto prepagato GIÀ salvato, ore/tariffa e riepilogo sono gestiti nella sezione Ricariche
            const hideRedundant = isPrepaid && !isNew;
            const fields = (hideRedundant ? spec.fields.filter((f) => !["ore_acquistate", "prezzo_unitario_ora"].includes(f.key)) : spec.fields).filter((f) => f.key !== "ubicazione");
            if (fields.length === 0) return null;
            return (
              <Card className="rounded-sm border-border shadow-none p-5" data-testid="contract-specifici-section">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-primary">{hideRedundant ? "Impostazioni monte ore & avvisi" : spec.titolo}</h3>
                {!hideRedundant && <ConsumoSummary consumo={form.consumo} />}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{fields.map(renderField)}</div>
              </Card>
            );
          })()}

          {/* Stampanti collegate (costo copia) */}
          {!isNew && form.categoria === "noleggio_costo_copia" && (
            <Card className="rounded-sm border-border shadow-none p-5" data-testid="contract-copiers-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-primary flex items-center gap-2"><Printer size={16} /> Stampanti collegate</h3>
                <Button variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="contract-manage-copiers-btn" onClick={() => setCcSheetOpen(true)}>Gestisci stampanti collegate</Button>
              </div>
              {ccDevices === null ? (
                <p className="text-sm text-muted-foreground">Caricamento…</p>
              ) : ccDevices.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nessuna stampante collegata. Usa "Gestisci stampanti collegate" per collegarne una.</p>
              ) : (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Dispositivo</TableHead><TableHead>Tipologia</TableHead>
                    <TableHead className="text-right">Canone</TableHead><TableHead className="text-right">Minimo</TableHead><TableHead>Stato</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {ccDevices.map((d) => (
                      <TableRow key={d.id} data-testid="contract-copier-row">
                        <TableCell className="text-xs font-medium">{d.device_marca} {d.device_modello}<div className="text-[10px] text-muted-foreground">{d.device_serial}</div></TableCell>
                        <TableCell className="text-xs">{CC_TIP_LABEL[d.tipologia] || d.tipologia}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">€ {d.canone_dispositivo}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">€ {d.minimo_fatturabile}</TableCell>
                        <TableCell><Badge variant="outline" className="rounded-sm text-[10px]">{d.stato}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </Card>
          )}

          {/* Allegati */}
          <DropZone onFiles={(fl) => uploadFiles({ target: { files: fl, value: "" } })} className="block">
          <Card className="rounded-sm border-border shadow-none p-5" data-testid="contract-allegati-section">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-primary">Allegati</h3>
              {!isNew && (<>
                <input type="file" multiple ref={fileRef} className="hidden" onChange={uploadFiles} />
                <Button variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="contract-upload-allegato" onClick={() => fileRef.current?.click()}><Upload size={14} /> Carica file</Button>
              </>)}
            </div>
            {isNew ? (
              <p className="text-sm text-muted-foreground">Salva il contratto per poter caricare allegati.</p>
            ) : (form.allegati || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessun allegato. Gli allegati sono legati a questo contratto e al relativo cliente (non inclusi nei PDF rapportino).</p>
            ) : (
              <div className="space-y-2">
                {(form.allegati || []).map((a) => (
                  <div key={a.id} className="flex items-center gap-2 bg-secondary/50 rounded-sm p-2 text-sm" data-testid="contract-allegato-row">
                    <Paperclip size={15} className="text-muted-foreground shrink-0" />
                    <a href={`${API}/contracts/${id}/allegati/${a.id}/download`} className="flex-1 truncate hover:underline text-primary">{a.nome_originale}</a>
                    <span className="text-xs text-muted-foreground">{Math.round((a.size || 0) / 1024)} KB</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => delAllegato(a.id)}><Trash size={14} /></Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
          </DropZone>

          {/* Ricariche monte ore + valorizzazione (solo Assistenza Prepagato) */}
          {!isNew && form.categoria === "assistenza" && form.tipo === "Contratto di Assistenza Prepagato" && form.monte_ore?.support_contract_id && (
            <ContractRicariche scId={form.monte_ore.support_contract_id} valorizzazione={form.valorizzazione} onChange={reloadValorizzazione} speseExtra={speseTot} />
          )}

          {/* Interventi collegati */}
          {!isNew && form.categoria !== "noleggio_costo_copia" && (
            <Card className="rounded-sm border-border shadow-none p-5" data-testid="contract-interventi-section">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider mb-3 text-primary">Interventi collegati</h3>
              {interventi.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nessun intervento collegato a questo contratto.</p>
              ) : (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Numero</TableHead><TableHead>Data</TableHead><TableHead>Tecnico/i</TableHead>
                    <TableHead>Richiesta</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Ore tot.</TableHead><TableHead className="text-right">Ore cont.</TableHead>{isFlat && <TableHead className="text-right">Tariffa eff.</TableHead>}{isFlat && (form.sottocontratti || []).length > 0 && <TableHead>Sottocontratto</TableHead>}<TableHead className="text-right">{isFlat ? "Val. rendic." : "Val. maturato"}</TableHead><TableHead className="text-right">Azioni</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {interventi.map((a) => (
                      <TableRow key={a.id} data-testid="contract-intervento-row" className="hover:bg-secondary">
                        <TableCell className="font-mono text-xs font-medium whitespace-nowrap">{a.numero || "—"}</TableCell>
                        <TableCell className="tabular-nums whitespace-nowrap">{fmtDate(a.data)}</TableCell>
                        <TableCell className="text-muted-foreground max-w-[140px] truncate">{a.tecnici || "—"}</TableCell>
                        <TableCell className="max-w-[220px] truncate text-muted-foreground">{a.richiesta || "—"}</TableCell>
                        <TableCell className="capitalize whitespace-nowrap">{a.stato}</TableCell>
                        <TableCell className="text-right tabular-nums">{a.ore_totali}h</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{a.stato === "completata" ? `${a.ore_conteggiate}h` : "—"}{isFlat && a.ore_escluse ? <span className="text-[10px] text-muted-foreground block">({a.ore_escluse}h fuori periodo)</span> : null}</TableCell>
                        {isFlat && <TableCell className="text-right tabular-nums">{a.tariffa_effettiva != null ? `${euro(a.tariffa_effettiva)}/h` : "—"}</TableCell>}
                        {isFlat && (form.sottocontratti || []).length > 0 && <TableCell className="font-mono text-[11px] whitespace-nowrap text-primary" data-testid="contract-intervento-sotto">{a.sottocontratto_codice || "—"}</TableCell>}
                        <TableCell className="text-right tabular-nums font-medium text-amber-600">
                          {a.valore_maturato != null ? <span title={a.num_lotti > 1 ? `${a.num_lotti} lotti` : ""}>{euro(a.valore_maturato)}{a.num_lotti > 1 ? ` · ${a.num_lotti} lotti` : ""}{a.sforamento ? " ⚠" : ""}</span> : "—"}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Apri intervento" data-testid="contract-intervento-open" onClick={() => nav(`/interventi/${a.id}`)}><ArrowSquareOut size={16} /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" title="Report PDF" data-testid="contract-intervento-pdf" onClick={() => openAuthPdf(`/attivita/${a.id}/pdf`)}><FilePdf size={16} /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </Card>
          )}

          {/* Storico / note */}
          <Card className="rounded-sm border-border shadow-none p-5" data-testid="contract-storico-section">
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wider mb-3 text-primary">Storico / note contratto</h3>
            <Textarea value={form.storico_note} onChange={(e) => upd("storico_note", e.target.value)} className="rounded-sm" rows={3} placeholder="Annotazioni, variazioni, promemoria sul contratto…" data-testid="contract-storico-note" />
            {!isNew && form.created_at && <p className="text-xs text-muted-foreground mt-2">Creato il {new Date(form.created_at).toLocaleString("it-IT")}</p>}
          </Card>
        </div>
      </div>
      {ccSheetOpen && <ContractSheet contractId={id} clients={clients} presetDeviceId={null} autoAddDevice={false} onClose={() => { setCcSheetOpen(false); loadCcDevices(); }} />}
    </InternalLayout>
  );
}


export function _consumoAlert(c) {
  if (!c) return null;
  if (c.categoria === "assistenza" && c.saldo_negativo) return "Attenzione: il contratto prepagato risulta con saldo ore negativo.";
  if (c.categoria === "flat" && c.superato) return "Attenzione: superato il monte ore massimo del periodo FLAT.";
  if (c.categoria === "manutenzione" && c.superato) return "Attenzione: superato il numero di interventi inclusi nel contratto.";
  return null;
}

const CBox = ({ label, value, tone = "" }) => (
  <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div><div className={`font-heading font-bold ${tone}`}>{value}</div></div>
);

function ConsumoSummary({ consumo: c }) {
  if (!c) return null;
  if (c.categoria === "assistenza") {
    return (
      <div className="grid grid-cols-3 gap-3 mb-4 bg-secondary/50 rounded-sm p-3 text-sm" data-testid="contract-consumo-box">
        <CBox label="Ore acquistate" value={`${c.ore_acquistate}h`} />
        <CBox label="Ore usate" value={`${c.ore_usate}h`} />
        <CBox label="Ore residue" value={`${c.ore_residue}h`} tone={c.saldo_negativo ? "text-rose-600" : "text-primary"} />
      </div>
    );
  }
  if (c.categoria === "flat") {
    const tipol = c.tipologia === "a_progetto" ? "A progetto" : "Mensile";
    return (
      <div className="mb-4 bg-secondary/50 rounded-sm p-3 text-sm" data-testid="contract-consumo-box">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CBox label="Tipologia" value={tipol} />
          <CBox label="Ricavo FLAT" value={c.ricavo_flat != null ? `€ ${c.ricavo_flat}` : (c.importo_periodo ? `€ ${c.importo_periodo}` : "—")} />
          <CBox label="Monte ore max" value={c.monte_ore_massimo ? `${c.monte_ore_massimo}h` : "—"} />
          <CBox label="Ore completate valide" value={`${c.ore_valide ?? c.ore_periodo ?? 0}h`} tone={c.superato ? "text-rose-600" : ""} />
          <CBox label="Ore residue" value={c.ore_residue != null ? `${c.ore_residue}h` : "—"} tone={c.superato ? "text-rose-600" : ""} />
          <CBox label="Tariffa oraria eff." value={c.tariffa_oraria_effettiva != null ? `€ ${c.tariffa_oraria_effettiva}/h` : "—"} />
          {c.spese_extra_totale > 0 && <CBox label="Spese extra" value={`− ${euro(c.spese_extra_totale)}`} tone="text-rose-600" />}
          <CBox label="Valore rendicontato" value={c.valore_rendicontato != null ? euro(c.valore_rendicontato) : euro(0)} tone="text-amber-600" />
          <CBox label="Superamento" value={c.superato ? `+${c.ore_superamento}h` : "—"} tone={c.superato ? "text-rose-600" : ""} />
        </div>
        <div className="text-[11px] text-muted-foreground mt-2">Periodo di competenza: {c.periodo_inizio || "—"} → {c.periodo_fine || "—"}</div>
        {c.superato && (
          <div className="mt-2 rounded-sm border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 flex items-center gap-2 text-xs" data-testid="flat-superamento-alert">
            <WarningCircle size={14} weight="fill" /> Superamento monte ore massimo: +{c.ore_superamento}h (la tariffa effettiva continua ad abbassarsi).
          </div>
        )}
      </div>
    );
  }
  if (c.categoria === "manutenzione") {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 bg-secondary/50 rounded-sm p-3 text-sm" data-testid="contract-consumo-box">
        <CBox label="Interventi inclusi" value={c.interventi_inclusi || "—"} />
        <CBox label="Interventi eseguiti" value={c.interventi_eseguiti} tone={c.superato ? "text-rose-600" : ""} />
        <CBox label="Residui" value={c.interventi_residui != null ? c.interventi_residui : "—"} />
        <CBox label="Ore totali" value={`${c.ore_totali}h`} />
      </div>
    );
  }
  return null;
}
